import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "./App.css";
import ToastNotification from "./components/ToastNotification";
import { API_URL } from "./apiConfig";
import { bootstrapSession, setSession, getAccessToken, getRole } from "./auth";

const LOGO = "/logo-euro-privilege.png";

export default function App() {
  const navigate = useNavigate();

  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd]   = useState(false);
  const [remember, setRemember] = useState(true);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState("");

  const [toast, setToast] = useState({ show: false, title: "", message: "", variant: "success" });
  const showToast = (title, message, variant = "success") =>
    setToast({ show: true, title, message, variant });

  useEffect(() => {
    let active = true;
    (async () => {
      const restored = await bootstrapSession();
      if (!active || !restored) return;
      const role = getRole();
      if (getAccessToken() && role) {
        navigate(role === "admin" ? "/admin/dashboard" : role === "condo" ? "/condo" : "/consulta", { replace: true });
      }
    })();
    return () => {
      active = false;
    };
  }, [navigate]);

  async function handleLogin(e) {
    if (e) e.preventDefault();
    setError("");

    if (!email || !password) {
      setError("Preencha o e-mail e a senha.");
      return;
    }
    if (!API_URL) {
      setError("API_URL não definida. Confira seu .env");
      return;
    }

    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password, remember }),
      });

      const contentType = r.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        const text = await r.text();
        console.error("[Login] Resposta não é JSON:", text);
        setError("Resposta inválida do servidor. Verifique a configuração.");
        return;
      }

      const data = await r.json();

      if (!r.ok) {
        setError(data?.message || "Credenciais inválidas. Tente novamente.");
        return;
      }

      if (!data.token) {
        setError("Token não recebido. Contate o administrador.");
        return;
      }

      setSession(data.token, data.role, remember);

      showToast(
        "Login efetuado",
        data.role === "admin" ? "Bem-vindo, administrador!" : "Bem-vindo ao Euro Privilege!",
        "success"
      );

      setTimeout(() => {
        navigate(data.role === "admin" ? "/admin/dashboard" : data.role === "condo" ? "/condo" : "/consulta");
      }, 900);
    } catch (e) {
      console.error("[Login]", e);
      setError(`Falha ao conectar na API: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <div className="lp-bg" />
      <div className="lp-blob3" />

      <div className="lp-root">
        <div className="lp-shell">

          {/* ── Brand panel ── */}
          <aside className="lp-brand">
            <div className="lp-orb" />
            <img className="lp-brand-logo" src={LOGO} alt="Euro Privilege" />

            <div className="lp-brand-body">
              <span className="lp-eyebrow">
                <span className="lp-pip" />
                Clube de benefícios
              </span>
              <h2 className="lp-headline">
                Vantagens exclusivas para{" "}
                <em>parceiros e condôminos</em>.
              </h2>
              <p className="lp-sub">
                O sistema de consulta da Euro Condomínios reúne ativações,
                parceiros e campanhas do clube em um só lugar.
              </p>
              <ul className="lp-feats">
                <li>
                  <span className="lp-feat-ic">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 6 9 17l-5-5"/>
                    </svg>
                  </span>
                  Consulta de parceiros e ativações em tempo real
                </li>
                <li>
                  <span className="lp-feat-ic">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="4" width="18" height="16" rx="2"/><path d="M3 9h18M8 4v16"/>
                    </svg>
                  </span>
                  Campanhas e comunicados centralizados
                </li>
                <li>
                  <span className="lp-feat-ic">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 2 4 5v6c0 5 3.5 8 8 11 4.5-3 8-6 8-11V5l-8-3Z"/>
                    </svg>
                  </span>
                  Acesso seguro e restrito a parceiros
                </li>
              </ul>
            </div>
          </aside>

          {/* ── Form panel ── */}
          <main className="lp-form-panel">
            <img className="lp-form-logo" src={LOGO} alt="Euro Privilege" />

            <div>
              <h1>Sistema de Consulta</h1>
              <p className="lp-subtitle">Acesso exclusivo para parceiros.<br/>Entre com suas credenciais.</p>
            </div>

            {error && (
              <div className="lp-error visible" role="alert">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{width:16,height:16,flexShrink:0}}>
                  <circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/>
                </svg>
                {error}
              </div>
            )}

            <form className="lp-form" onSubmit={handleLogin} noValidate>

              <div className="lp-field">
                <label className="lp-label" htmlFor="email">E-mail</label>
                <div className="lp-input-wrap">
                  <svg className="lp-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 6 9-6"/>
                  </svg>
                  <input
                    id="email" type="email" placeholder="seu@email.com"
                    value={email} onChange={e => { setEmail(e.target.value); setError(""); }}
                    autoComplete="email" autoFocus
                  />
                </div>
              </div>

              <div className="lp-field">
                <label className="lp-label" htmlFor="senha">Senha</label>
                <div className="lp-input-wrap">
                  <svg className="lp-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="4" y="11" width="16" height="9" rx="2"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/>
                  </svg>
                  <input
                    id="senha" type={showPwd ? "text" : "password"} placeholder="••••••••"
                    value={password} onChange={e => { setPassword(e.target.value); setError(""); }}
                    autoComplete="current-password"
                  />
                  <button type="button" className="lp-reveal" onClick={() => setShowPwd(v => !v)}
                          aria-label={showPwd ? "Ocultar senha" : "Mostrar senha"}>
                    {showPwd ? (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z"/>
                        <circle cx="12" cy="12" r="3"/>
                        <line x1="3" y1="3" x2="21" y2="21"/>
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <label className="lp-remember">
                <input type="checkbox" checked={remember} onChange={e => setRemember(e.target.checked)} />
                <span className="lp-check">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 6 9 17l-5-5"/>
                  </svg>
                </span>
                Manter conectado neste dispositivo
              </label>

              <button type="submit" className={`lp-btn${loading ? " loading" : ""}`} disabled={loading}>
                <span className="lp-spinner" />
                <span className="lp-btn-label">Entrar</span>
                <svg className="lp-btn-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M13 6l6 6-6 6"/>
                </svg>
              </button>

            </form>

            <div className="lp-secure">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="4" y="11" width="16" height="9" rx="2"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/>
              </svg>
              Conexão segura · privilegeapp.euro.adm.br
            </div>
          </main>

        </div>
      </div>

      <ToastNotification
        show={toast.show}
        onClose={() => setToast(t => ({ ...t, show: false }))}
        title={toast.title}
        message={toast.message}
        variant={toast.variant}
      />
    </>
  );
}
